"use client";

import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Play, Pause } from 'lucide-react';

const videos = [
  {
    id: 'video-1',
    url: "https://d1twcv82c6wl0s.cloudfront.net/video-1.mp4",
    title: "Medieval Battle",
    thumbnail: "https://d1twcv82c6wl0s.cloudfront.net/image-1.png",
  },
  {
    id: 'video-2',
    url: "https://d1twcv82c6wl0s.cloudfront.net/video-2.mp4",
    title: "Family Man",
    thumbnail: "https://d1twcv82c6wl0s.cloudfront.net/image-2.png",
  },
  {
    id: 'video-3',
    url: "https://d1twcv82c6wl0s.cloudfront.net/video-3.mp4",
    title: "Robots vs Robots",
    thumbnail: "https://d1twcv82c6wl0s.cloudfront.net/image-3.png",
  },
  {
    id: 'video-4',
    url: "https://d1twcv82c6wl0s.cloudfront.net/video-4.mp4",
    title: "Gangsters Busted",
    thumbnail: "https://d1twcv82c6wl0s.cloudfront.net/image-4.png",
  },
  {
    id: 'video-5',
    url: "https://d1twcv82c6wl0s.cloudfront.net/video-5.mp4",
    title: "Space Wars",
    thumbnail: "https://d1twcv82c6wl0s.cloudfront.net/image-5.png",
  },
];

const VideoThumbnail = ({ video, onClick, isActive }) => {
  return (
    <div onClick={onClick} className="relative cursor-pointer group">
      <div
        className="relative overflow-hidden rounded-lg video-shadow"
        style={{
          border: "2px solid #57ba98",
        }}
      >
        <img
          src={video.thumbnail}
          alt={video.title}
          className="w-full h-full aspect-video object-cover transition-transform duration-300 group-hover:scale-105 bg-gray-900"
        />
        <div
          className={`absolute inset-0 ${
            isActive
              ? "bg-teal-500/40"
              : "bg-teal-500/20 group-hover:bg-teal-500/30"
          } transition-colors`}
        />
        <div className="absolute inset-0 opacity-50 bg-gradient-to-t from-teal-500/20 via-transparent to-transparent" />
      </div>
      <p className="mt-2 text-white text-center">{video.title}</p>
    </div>
  );
};

const MetricBox = ({ label, value }) => (
  <div className="ms-20">
    <h3 className="text-white text-lg mb-2">{label}</h3>
    <button className="px-10 py-5 w-[300px] text-white rounded-lg font-medium relative group overflow-hidden button-shadow-user">
      <span className="relative z-10">{value}</span>
    </button>
  </div>
);

export default function Done() {
  const navigate = useNavigate();
  const [activeVideo, setActiveVideo] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [accuracy, setAccuracy] = useState("0%");
  const [score, setScore] = useState(0);
  const videoRef = useRef(null);

  useEffect(() => {
    // Load the video that was playing in home.jsx
    const savedVideo = localStorage.getItem('currentPlayingVideo');
    const savedTime = localStorage.getItem('videoCurrentTime');
    const totalResponse = localStorage.getItem("totalResponse");

    if (savedVideo) {
      const videoData = JSON.parse(savedVideo);
      setActiveVideo(videoData);

      // Set the video time after video loads
      if (savedTime && videoRef.current) {
        const timeToSet = parseFloat(savedTime);
        videoRef.current.addEventListener('loadedmetadata', () => {
          videoRef.current.currentTime = timeToSet;
        }, { once: true });
      }
    }

    // Load accuracy and score
    if (totalResponse) {
      try {
        const parsedResponse = JSON.parse(totalResponse);
        const parsedAccuracy = parseFloat(parsedResponse.accuracy) || 0;
        const parsedScore = parseFloat(parsedResponse.score) || 0;

        setAccuracy(`${parsedAccuracy.toFixed(2)}%`);
        setScore(Math.ceil(parsedScore));
      } catch (error) {
        console.error("Error parsing totalResponse from localStorage:", error);
      }
    }
  }, []);

  const handleVideoClick = (video) => {
    if (video.id !== activeVideo?.id) {
      setActiveVideo(video);
      setIsPlaying(false);
    }
  };

  const togglePlayPause = async () => {
    if (!videoRef.current) return;

    try {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        await videoRef.current.play();
        setIsPlaying(true);
      }
    } catch (error) {
      console.error("Error toggling play/pause:", error);
      setIsPlaying(false);
    }
  };

  return (
    <main className="min-h-screen w-full relative">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat -z-10"
        style={{
          backgroundImage: "url(../assets/images/user-login-bg.png)",
        }}
      />
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-8 heading-text-user">
          Media Sentinel
        </h1>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
          {videos.map((video) => (
            <VideoThumbnail
              key={video.id}
              video={video}
              onClick={() => handleVideoClick(video)}
              isActive={video.id === activeVideo?.id}
            />
          ))}
        </div>

        <div className="grid md:grid-cols-3 gap-8 mt-16">
          {/* Left side - Video Player */}
          <div className="col-span-2">
            <div className="relative aspect-video rounded-lg overflow-hidden bg-black/80">
              {activeVideo && (
                <>
                  <video
                    ref={videoRef}
                    className="absolute inset-0 w-full h-full object-cover"
                    playsInline
                  >
                    <source src={activeVideo.url} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>

                  <button
                    onClick={togglePlayPause}
                    className={`absolute inset-0 flex items-center justify-center transition-opacity duration-200 ${
                      isPlaying
                        ? "opacity-0 hover:opacity-100 bg-black/10 hover:bg-black/30"
                        : "bg-black/30"
                    } group`}
                  >
                    <div className="rounded-full p-4 bg-teal-600/80 group-hover:bg-teal-600 transition-colors">
                      {isPlaying ? (
                        <Pause className="w-12 h-12 text-white" />
                      ) : (
                        <Play className="w-12 h-12 text-white" />
                      )}
                    </div>
                  </button>

                  <div className="absolute inset-0 pointer-events-none opacity-50 bg-gradient-to-t from-teal-500/20 via-transparent to-transparent" />
                </>
              )}
            </div>
            {/* Display current video title */}
            {activeVideo && (
              <div className="mt-4 flex items-center gap-4 justify-center">
                <h3 className="text-white text-xl">{activeVideo.title}</h3>
              </div>
            )}
          </div>

          {/* Right side - Metrics */}
          <div className="space-y-6">
            <MetricBox label="ACCURACY" value={accuracy} />
            <MetricBox label="SCORE" value={score} />
          </div>
        </div>

        <div className="flex justify-end gap-4 mt-8">
          <button
            onClick={() => {
              localStorage.clear();
              navigate("/login");
            }}
            className="px-10 py-3 text-white rounded-lg font-medium relative group overflow-hidden button-shadow-user"
          >
            <span className="relative z-10">Back to Home</span>
          </button>
        </div>
      </div>
    </main>
  );
}

